<template>
  <div
    class="mb-2 spb-row"
    :class="{ 'spb-row--invalid': hasError }"
    data-cy="installment-row-wrapper"
  >
    <div class="d-flex align-items-center gap-2">
      <input
        :value="installment.label"
        type="text"
        class="form-control"
        placeholder="Label"
        :disabled="disabled"
        data-cy="installment-label"
        @input="onLabelInput"
      />

      <div
        class="input-group spb-row__value"
        :class="{ 'spb-row__value--invalid': Boolean(error) }"
        style="width: 140px; flex: 0 0 140px"
      >
        <span v-if="splitMode === 'amount'" class="input-group-text">$</span>
        <input
          :value="displayValue"
          type="number"
          min="0"
          :step="splitMode === 'percentage' ? '1' : '0.01'"
          class="form-control"
          :class="{ 'is-invalid': Boolean(error) }"
          :disabled="disabled"
          data-cy="installment-value"
          @focus="onValueFocus"
          @blur="onValueBlur"
          @input="onValueInput"
        />
        <span v-if="splitMode === 'percentage'" class="input-group-text"
          >%</span
        >
      </div>

      <span
        class="text-muted small"
        style="width: 90px; flex: 0 0 90px"
        data-cy="calculated-opposite"
      >
        {{ oppositeDisplay }}
      </span>

      <input
        :value="installment.due_date ?? ''"
        type="date"
        class="form-control"
        :class="{ 'is-invalid': Boolean(dueDateError) }"
        style="width: 160px; flex: 0 0 160px"
        :disabled="disabled"
        :required="!disabled"
        :min="minDate"
        data-cy="installment-due-date"
        @input="onDueDateInput"
      />

      <div class="spb-row__action">
        <button
          v-if="removable"
          type="button"
          class="btn btn-link text-danger p-0"
          :disabled="disabled"
          data-cy="remove-installment"
          :aria-label="`Remove ${installment.label}`"
          @click="$emit('remove')"
        >
          <i class="fas fa-trash-alt" />
        </button>
        <Tooltip v-else-if="locked" placement="top" width="240">
          <template #trigger>
            <span
              class="spb-row__lock"
              aria-label="Installment locked"
              data-cy="locked-installment"
              tabindex="0"
            >
              <i class="fas fa-lock" />
            </span>
          </template>
          <template #tooltip>
            <span>Historical installment - cannot be edited.</span>
          </template>
        </Tooltip>
      </div>
    </div>

    <div
      v-if="error"
      class="text-danger small mt-1"
      data-cy="installment-error"
    >
      {{ error }}
    </div>
    <div
      v-if="dueDateError"
      class="text-danger small mt-1"
      data-cy="installment-due-date-error"
    >
      {{ dueDateError }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import { dollars } from "@/mixins";

type SplitMode = "percentage" | "amount";

interface Installment {
  id?: number;
  label: string;
  label_overridden?: boolean;
  value: number;
  calculated_opposite_value: number;
  position: number;
  due_date?: string | null;
  _destroy?: boolean;
}

const props = defineProps<{
  installment: Installment;
  splitMode: SplitMode;
  removable: boolean;
  error?: string | null;
  dueDateError?: string | null;
  disabled?: boolean;
  locked?: boolean;
  minDate?: string;
}>();

const emit = defineEmits<{
  remove: [];
  "update:label": [value: string];
  "update:value": [value: number];
  "update:dueDate": [value: string | null];
}>();

const isValueFocused = ref(false);
const draftValue = ref("");

// Row-level highlight tracks whatever error is currently surfaced to the user.
// Value errors are already shown eagerly (the value input's is-invalid), so the
// tint just strengthens that; the due-date error is gated to a save attempt by
// the parent, so the tint follows the same timing for missing dates.
const hasError = computed(
  () => Boolean(props.error) || Boolean(props.dueDateError)
);

const displayValue = computed(() => {
  if (isValueFocused.value) return draftValue.value;
  if (props.splitMode === "amount") {
    return (props.installment.value / 100).toFixed(2);
  }
  return String(props.installment.value);
});

const oppositeDisplay = computed(() => {
  const v = props.installment.calculated_opposite_value;
  if (props.splitMode === "percentage") return dollars(v);
  return `${Number(v).toFixed(2)}%`;
});

function onLabelInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  emit("update:label", target.value);
}

function onValueFocus(event: FocusEvent): void {
  const target = event.target as HTMLInputElement;
  draftValue.value = target.value;
  isValueFocused.value = true;
}

function onValueBlur(): void {
  isValueFocused.value = false;
  draftValue.value = "";
}

function onValueInput(event: Event): void {
  const raw = (event.target as HTMLInputElement).value;
  draftValue.value = raw;
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return;
  // Percentages are whole integers; amounts are cents (dollars * 100).
  const stored =
    props.splitMode === "amount"
      ? Math.round(parsed * 100)
      : Math.round(parsed);
  emit("update:value", stored);
}

function onDueDateInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value;
  emit("update:dueDate", value === "" ? null : value);
}
</script>

<style lang="scss" scoped>
.spb-row--invalid {
  border-left: 3px solid var(--danger-color, #dc3545);
  background-color: rgba(220, 53, 69, 0.04);
  border-radius: 4px;
  padding: 0.5rem 0.5rem 0.25rem;
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}

.spb-row__value {
  background-color: #fff;
  border: 2px solid #9aa3ad;
  border-radius: 0.25rem;
  overflow: hidden;
}

.spb-row__value--invalid {
  border-color: var(--danger-color, #dc3545);
}

.spb-row__value > .form-control,
.spb-row__value > .input-group-text {
  background-color: transparent;
  border: 0;
  border-radius: 0;
  min-width: 0;
}

.spb-row__value > .form-control:focus {
  box-shadow: none;
}

.spb-row__value > .input-group-text {
  color: #6c757d;
  font-size: 0.9rem;
  padding: 0 0.5rem;
}

.spb-row__value > .input-group-text:first-child {
  border-right: 1px solid #d5dbe1;
}

.spb-row__value > .form-control + .input-group-text {
  border-left: 1px solid #d5dbe1;
}

.spb-row__action {
  width: 32px;
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spb-row__action .btn {
  align-items: center;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  line-height: 1;
  width: 24px;
}

.spb-row__action :deep(.tooltip-wrapper) {
  height: 24px;
}

.spb-row__lock {
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}
</style>
